import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { apiUrl } from '../hooks/useOnboardingStatus';
import { capture } from '../analytics';

function PlatformIcon({ platform }) {
  if (platform === 'windows') {
    return (
      <svg viewBox="0 0 32 32" aria-hidden="true">
        <path d="M4 6.5 14 5v10H4V6.5Zm12-1.8L28 3v12H16V4.7ZM4 17h10v10L4 25.6V17Zm12 0h12v12l-12-1.7V17Z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      <path d="M21.3 5.2c1.2-1.4 3.2-2.4 4.9-2.5.2 2-.5 4-1.7 5.4-1.2 1.4-3.1 2.4-4.9 2.2-.2-1.9.5-3.8 1.7-5.1ZM26.8 17.6c0-3.1 2.6-4.6 2.7-4.7-1.5-2.2-3.8-2.5-4.6-2.5-2-.2-3.8 1.2-4.8 1.2-1 0-2.5-1.2-4.2-1.1-2.1 0-4.1 1.3-5.2 3.2-2.2 3.9-.6 9.6 1.6 12.7 1.1 1.5 2.3 3.2 4 3.1 1.6-.1 2.2-1 4.2-1s2.5 1 4.2 1c1.7 0 2.8-1.5 3.8-3.1 1.2-1.8 1.7-3.5 1.8-3.6-.1 0-3.5-1.3-3.5-5.2Z" />
      </svg>
    );
}

function DownloadPage({
  onboardingStatus = 'idle',
  subscription = null,
  refreshOnboarding = () => {},
}) {
  const {
    isAuthenticated,
    user,
    loginWithRedirect,
    getAccessTokenSilently,
  } = useAuth0();
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [loadingPlatform, setLoadingPlatform] = useState('');
  const [availability, setAvailability] = useState(null);

  const detectedPlatform = useMemo(() => {
    if (typeof navigator === 'undefined') return null;
    const value = `${navigator.userAgent || ''} ${navigator.platform || ''}`;
    if (/Windows/i.test(value)) return 'windows';
    if (/Mac/i.test(value)) return 'mac';
    return null;
  }, []);

  useEffect(() => {
    let active = true;
    fetch(apiUrl('/beta/status'))
      .then((response) => {
        if (!response.ok) throw new Error('beta_status_unavailable');
        return response.json();
      })
      .then((payload) => {
        if (active) setAvailability(payload);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) refreshOnboarding();
  }, [isAuthenticated, refreshOnboarding]);

  useEffect(() => {
    capture('download_page_viewed', {
      platform: detectedPlatform || 'unknown',
    });
  }, [detectedPlatform]);

  const download = async (platform) => {
    try {
      setLoadingPlatform(platform);
      setStatus('Preparing download...');
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });
      const res = await fetch(
        `${apiUrl('/release')}?platform=${encodeURIComponent(platform)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        const messages = {
          beta_seat_required: 'Your beta seat is still being activated. Try again in a moment.',
          eligible_subscription_required:
            'An active trial or subscription is required to download Virgil.',
          download_rate_limited:
            'Too many download requests. Please wait before trying again.',
          release_not_found:
            'That build is temporarily unavailable. Please contact support.',
        };
        throw new Error(messages[payload.error] || 'The download could not be prepared.');
      }
      const data = await res.json();
      if (!data.downloadUrl) throw new Error('No download URL in response');
      capture('download_started', { platform });
      setStatus(`Virgil ${data.version || ''} is downloading.`.trim());
      window.location.assign(data.downloadUrl);
    } catch (e) {
      setStatus(e.message || 'The download could not be prepared.');
    } finally {
      setLoadingPlatform('');
    }
  };

  const startSignup = () => {
    capture('signup_started');
    loginWithRedirect({
      appState: { returnTo: '/download' },
      authorizationParams: { screen_hint: 'signup', prompt: 'select_account' },
    });
  };

  const renderAccessState = () => {
    if (!isAuthenticated) {
      return (
        <div className="download-result-block download-result-block--signin">
          <span className="download-panel-eyebrow">Your access</span>
          <h2>Sign in to download</h2>
          <p>Create an account, verify your email, and start a trial. Then both desktop builds are available here.</p>
          <div className="download-options-grid">
            <button className="cta-button" type="button" onClick={startSignup}>
              Start your 14-day trial
            </button>
            <button
              className="cta-button secondary"
              type="button"
              onClick={() =>
                loginWithRedirect({
                  appState: { returnTo: '/download' },
                  authorizationParams: { screen_hint: 'login' },
                })
              }
            >
              Log in
            </button>
          </div>
          <p className="download-access-footnote">
            Card required for the trial. Cancel before day 14 to avoid the first monthly charge.
          </p>
        </div>
      );
    }

    if (!user?.email_verified) {
      return (
        <div className="download-result-block download-result-block--pending">
          <span className="download-panel-eyebrow">One step left</span>
          <h2>Verify your email</h2>
          <p>Use the link Auth0 sent to {user?.email || 'your inbox'}, then return here.</p>
        </div>
      );
    }

    if (onboardingStatus === 'loading' || onboardingStatus === 'idle') {
      return (
        <div className="download-result-block download-result-block--checking" role="status">
          <span className="download-access-spinner" aria-hidden="true" />
          <p>Checking your beta access…</p>
        </div>
      );
    }

    if (onboardingStatus === 'error') {
      return (
        <div className="download-result-block download-result-block--error">
          <span className="download-panel-eyebrow">Connection interrupted</span>
          <p role="alert">We could not check your beta access.</p>
          <button className="cta-button" type="button" onClick={refreshOnboarding}>
            Try again
          </button>
        </div>
      );
    }

    if (onboardingStatus !== 'eligible') {
      const betaIsFull = availability?.full;
      return (
        <div className="download-result-block download-result-block--plan">
          <span className="download-panel-eyebrow">{betaIsFull ? 'Capacity reached' : 'Choose your access'}</span>
          <h2>{betaIsFull ? 'The public beta is full' : 'Start a trial to download'}</h2>
          <p>
            {betaIsFull
              ? 'All 100 seats are currently reserved or claimed. Check back after abandoned reservations expire.'
              : 'Choose Starter, Pro, or Studio. Your beta seat is claimed when Stripe starts the trial.'}
          </p>
          {!betaIsFull && (
            <button className="cta-button" type="button" onClick={() => navigate('/pricing')}>
              Choose a plan
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="download-result-block download-result-block--ready">
        <div className="download-ready-status">
          <span aria-hidden="true" />
          Access ready{subscription?.planName ? ` · ${subscription.planName}` : ''}
        </div>
        <h2>Choose your build</h2>
        <p>
          {detectedPlatform
            ? `We detected ${detectedPlatform === 'mac' ? 'macOS' : 'Windows'} and marked the matching build.`
            : 'Use the same Virgil account on either supported platform.'}
        </p>
        <div className="download-platform-buttons">
          {['mac', 'windows'].map((platform) => (
            <button
              key={platform}
              className={`download-platform-button${detectedPlatform === platform ? ' is-recommended' : ''}`}
              onClick={() => download(platform)}
              disabled={Boolean(loadingPlatform)}
              type="button"
              aria-label={`Download Virgil for ${
                platform === 'mac' ? 'macOS (Apple silicon)' : 'Windows'
              }${detectedPlatform === platform ? ' (recommended)' : ''}`}
            >
              <span className="download-platform-icon">
                <PlatformIcon platform={platform} />
              </span>
              <span className="download-platform-copy">
                <small>{detectedPlatform === platform ? 'Recommended for this device' : 'Desktop build'}</small>
                <strong>{loadingPlatform === platform ? 'Preparing…' : platform === 'mac' ? 'macOS' : 'Windows'}</strong>
                <span>{platform === 'mac' ? 'macOS 13+ · Apple silicon · DMG' : 'Windows · ZIP'}</span>
              </span>
              <span className="download-platform-arrow" aria-hidden="true">↓</span>
            </button>
          ))}
        </div>
        {status && <p className="alpha-notice download-status-note" role="status">{status}</p>}
        <div className="download-info">
          <h3>Install Virgil on macOS</h3>
          <ol>
            <li>Open the downloaded DMG.</li>
            <li>Drag the Virgil icon onto the Applications folder in the installer window.</li>
            <li>Open Virgil from Applications and sign in with this Auth0 account.</li>
          </ol>
          <h3>Install Virgil on Windows</h3>
          <ol>
            <li>Open the downloaded ZIP file.</li>
            <li>Open the Virgil installer inside it and follow the installation prompts.</li>
            <li>Open Virgil and sign in with this Auth0 account.</li>
          </ol>
          <p>
            The macOS build requires macOS 13 or newer and Apple silicon (M1 or newer).
            macOS and Windows may show an operating-system confirmation the
            first time you open a downloaded app.
          </p>
        </div>
      </div>
    );
  };

  const hasSeatCounts =
    Number.isFinite(Number(availability?.remaining)) &&
    Number.isFinite(Number(availability?.capacity));
  const seatStatus = availability?.full
    ? 'Beta at capacity'
    : hasSeatCounts
      ? `${availability.remaining} of ${availability.capacity} seats open`
    : '100-seat public beta';

  return (
    <div className="page-container download-page-shell">
      <SEO 
        title="Download Virgil for Apple Silicon macOS or Windows"
        description="Authenticated Virgil public-beta downloads for Apple silicon macOS and Windows."
        keywords="download AI writer, AI writing software download, screenwriting software download, AI writer app"
        noindex
      />
      <section className="content-section download-page-section">
        <div className="download-page-layout">
          <header className="download-page-intro">
            <div className="download-page-kicker">
              <span aria-hidden="true" />
              Virgil desktop
            </div>
            <h1 className="download-page-title">Take the studio with you.</h1>
            <p className="download-page-lede">
              The full Virgil workspace for long drafts, visual passes, and focused writing, built for Apple silicon and Windows.
            </p>

            <div className="download-platform-summary" aria-label="Supported platforms">
              <div>
                <span className="download-summary-icon"><PlatformIcon platform="mac" /></span>
                <span><strong>macOS</strong><small>Apple silicon</small></span>
              </div>
              <div>
                <span className="download-summary-icon"><PlatformIcon platform="windows" /></span>
                <span><strong>Windows</strong><small>Desktop build</small></span>
              </div>
            </div>

            <div className="download-page-proof">
              <div>
                <strong>One account</strong>
                <span>Your beta seat works across both platforms.</span>
              </div>
              <div>
                <strong>Private downloads</strong>
                <span>Each release link is short-lived and tied to your access.</span>
              </div>
              <div>
                <strong>Public beta</strong>
                <span>An active trial or subscription unlocks the latest build.</span>
              </div>
            </div>
          </header>

          <aside className="download-access-card" aria-label="Virgil download access">
            <div className="download-access-card-header">
              <span>Download access</span>
              <span className={`download-seat-status${availability?.full ? ' is-full' : ''}`}>
                <i aria-hidden="true" />
                {seatStatus}
              </span>
            </div>
            {renderAccessState()}
            <div className="download-access-support">
              Need help? <a href="mailto:support@virgil.com.ai">Contact support</a>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

export default DownloadPage;

